document.documentElement.classList.remove('no-js');

const moneyText = (value) => value || '';

function initMenu() {
  const toggle = document.querySelector('[data-menu-toggle]');
  const menu = document.querySelector('[data-mobile-menu]');
  const header = document.querySelector('[data-site-header]');
  if (!toggle || !menu) return;

  const toggleLabel = toggle.querySelector('.sr-only');
  const desktopMedia = window.matchMedia('(min-width: 861px)');

  const syncHeaderHeight = () => {
    const headerHeight = Math.ceil(header?.getBoundingClientRect().height || 0);
    if (headerHeight > 0) document.documentElement.style.setProperty('--site-header-height', `${headerHeight}px`);
  };

  syncHeaderHeight();
  window.addEventListener('resize', syncHeaderHeight, { passive: true });
  if ('ResizeObserver' in window && header) new ResizeObserver(syncHeaderHeight).observe(header);

  const setMenuOpen = (open, { restoreFocus = false } = {}) => {
    toggle.setAttribute('aria-expanded', String(open));
    menu.hidden = !open;
    document.body.classList.toggle('menu-open', open);
    if (toggleLabel) toggleLabel.textContent = open ? 'Stäng meny' : 'Öppna meny';

    if (open) {
      window.requestAnimationFrame(() => menu.querySelector('a')?.focus({ preventScroll: true }));
    } else if (restoreFocus) {
      toggle.focus({ preventScroll: true });
    }
  };

  toggle.addEventListener('click', () => {
    setMenuOpen(toggle.getAttribute('aria-expanded') !== 'true');
  });

  menu.addEventListener('click', (event) => {
    if (event.target.closest('a')) setMenuOpen(false);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
      setMenuOpen(false, { restoreFocus: true });
    }
  });

  desktopMedia.addEventListener('change', (event) => {
    if (event.matches) setMenuOpen(false);
  });
}

function initLegacyDesigner(root) {
  const form = root.querySelector('form');
  const textInput = root.querySelector('[data-text-input]');
  const previewText = root.querySelector('[data-preview-text]');
  const previewArt = root.querySelector('[data-preview-art]');
  const symbolPreview = root.querySelector('[data-preview-social]');
  const variantInput = root.querySelector('[data-variant-input]');
  const widthProperty = root.querySelector('[data-width-property]');
  const specificationProperty = root.querySelector('[data-specification-property]');
  const productionMeasureProperty = root.querySelector('[data-production-measure-property]');
  const productionFileInput = root.querySelector('[data-production-file]');
  const designIdProperty = root.querySelector('[data-design-id-property]');
  const productionError = root.querySelector('[data-production-error]');
  const submitButton = root.querySelector('[data-designer-submit]');
  const submitLabel = root.querySelector('[data-submit-label]');
  const widthMm = root.querySelector('[data-width-mm]');
  const heightMm = root.querySelector('[data-height-mm]');
  const price = root.querySelector('[data-product-price]');
  const unitPrice = root.querySelector('[data-unit-price]');
  const quantityInput = root.querySelector('[data-quantity-input]');
  const characterCount = root.querySelector('[data-character-count]');
  const summaryText = root.querySelector('[data-summary-text]');
  const summaryDetails = root.querySelector('[data-summary-details]');
  const preview = root.querySelector('[data-preview]');
  const mirrorInput = root.querySelector('[data-mirror-input]');
  const vectorizer = window.SwedeDecalVectorizer;
  const previewMeasureContext = document.createElement('canvas').getContext('2d');
  let productionReady = false;

  if (form) {
    form.enctype = 'multipart/form-data';
    form.encoding = 'multipart/form-data';
  }

  const selectedFont = () => root.querySelector('[data-font-input]:checked');
  const selectedColor = () => root.querySelector('[data-color-input]:checked');
  const selectedSymbol = () => root.querySelector('[data-symbol-input]:checked');
  const selectedVariant = () => root.querySelector('[data-variant-button].is-selected');
  const formatSek = (cents) => new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK', maximumFractionDigits: 0 }).format(cents / 100);

  const invalidateProductionFile = () => {
    productionReady = false;
    if (productionFileInput?.files?.length) productionFileInput.value = '';
    if (productionError) productionError.hidden = true;
  };

  const alignPreviewSymbol = () => {
    if (!symbolPreview || symbolPreview.hidden) return;
    const fallbackOffset = -0.08 * Number.parseFloat(getComputedStyle(previewText || previewArt).fontSize || '0');
    let offset = fallbackOffset;

    if (previewMeasureContext && previewText) {
      const style = getComputedStyle(previewText);
      previewMeasureContext.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
      const metrics = previewMeasureContext.measureText(previewText.textContent || 'DIN TEXT');
      const values = [metrics.fontBoundingBoxAscent, metrics.fontBoundingBoxDescent, metrics.actualBoundingBoxAscent, metrics.actualBoundingBoxDescent];
      if (values.every(Number.isFinite)) {
        offset = (metrics.fontBoundingBoxAscent - metrics.fontBoundingBoxDescent + metrics.actualBoundingBoxDescent - metrics.actualBoundingBoxAscent) / 2;
      }
    }

    symbolPreview.style.setProperty('--symbol-offset', `${offset.toFixed(2)}px`);
  };

  const fitPreview = () => {
    if (!previewArt?.parentElement) return;
    const available = Math.max(220, previewArt.parentElement.clientWidth - 72);
    const symbolValue = selectedSymbol()?.value || 'Ingen';
    const fontValue = selectedFont()?.value || 'Josefin Sans';
    const textLength = Math.max(4, (textInput?.value.trim() || 'DIN TEXT').length + (symbolValue !== 'Ingen' ? 3 : 0));
    const scriptFont = ['Pacifico', 'Dancing Script'].includes(fontValue);
    const widthFactor = scriptFont ? 0.88 : 0.67;
    const size = Math.min(86, Math.max(18, available / (textLength * widthFactor)));
    previewArt.style.fontSize = `${size}px`;
  };

  const updateMeasurements = () => {
    const width = Number(widthMm?.textContent || 100);
    const text = textInput?.value.trim() || 'DIN TEXT';
    const density = Math.max(2.2, Math.min(6.8, text.length * 0.28));
    const fontFactor = ['Pacifico', 'Dancing Script'].includes(selectedFont()?.value) ? 1.25 : 1;
    const calculated = Math.max(18, Math.round((width / density) * fontFactor));
    if (heightMm) heightMm.textContent = String(calculated);
  };

  function updateSummary() {
    const font = selectedFont();
    const color = selectedColor();
    const symbol = selectedSymbol();
    const variant = selectedVariant();
    const text = textInput?.value.trim() || 'DIN TEXT';
    const width = variant?.dataset.width ? `${variant.dataset.width} cm` : '';
    if (summaryText) summaryText.textContent = text;
    if (summaryDetails) summaryDetails.textContent = [font?.value, color?.value, width, symbol?.value === 'Ingen' ? 'Ingen symbol' : symbol?.value, mirrorInput?.checked ? 'Insida glas' : null].filter(Boolean).join(' · ');
    if (specificationProperty) specificationProperty.value = [font?.dataset.code, color?.dataset.code, width, symbol?.value === 'Ingen' ? 'NO-SYMBOL' : symbol?.value?.toUpperCase(), mirrorInput?.checked ? 'MIRROR' : 'NORMAL'].filter(Boolean).join('-');
  }

  const updateText = () => {
    invalidateProductionFile();
    if (previewText) previewText.textContent = textInput.value.trim() || 'DIN TEXT';
    if (characterCount) characterCount.textContent = String(textInput.value.length);
    updateMeasurements();
    updateSummary();
    window.requestAnimationFrame(() => {
      fitPreview();
      alignPreviewSymbol();
    });
  };

  const updateFont = () => {
    invalidateProductionFile();
    const input = selectedFont();
    root.querySelectorAll('.font-choice').forEach((choice) => choice.classList.toggle('is-selected', choice.contains(input)));
    if (previewArt && input) previewArt.style.fontFamily = input.dataset.font || input.value;
    updateMeasurements();
    updateSummary();
    window.requestAnimationFrame(() => {
      fitPreview();
      alignPreviewSymbol();
    });
  };

  const updateSymbol = () => {
    invalidateProductionFile();
    const input = selectedSymbol();
    const icon = input?.value && input.value !== 'Ingen' ? vectorizer?.iconMarkup(input.value) : '';
    root.querySelectorAll('.symbol-choice').forEach((choice) => choice.classList.toggle('is-selected', choice.contains(input)));
    if (symbolPreview) {
      symbolPreview.hidden = !icon;
      symbolPreview.innerHTML = icon || '';
      const metrics = vectorizer?.symbolMetrics?.(input?.value);
      symbolPreview.style.setProperty('--symbol-aspect', String(metrics?.aspect || 1));
    }
    updateMeasurements();
    updateSummary();
    window.requestAnimationFrame(() => {
      fitPreview();
      alignPreviewSymbol();
    });
  };

  const updatePrice = () => {
    const variant = selectedVariant();
    const cents = Number(variant?.dataset.priceCents || 0);
    const quantity = Math.max(1, Number.parseInt(quantityInput?.value || '1', 10) || 1);
    if (quantityInput) quantityInput.value = String(Math.min(99, quantity));
    if (price) price.textContent = cents ? formatSek(cents * Math.min(99, quantity)) : moneyText(variant?.dataset.price);
    if (unitPrice) unitPrice.textContent = `${moneyText(variant?.dataset.price)} / st`;
  };

  textInput?.addEventListener('input', updateText);
  root.querySelectorAll('[data-font-input]').forEach((input) => input.addEventListener('change', updateFont));
  root.querySelectorAll('[data-color-input]').forEach((input) => {
    input.addEventListener('change', () => {
      invalidateProductionFile();
      if (previewArt) previewArt.style.color = input.dataset.color;
      updateSummary();
    });
  });
  root.querySelectorAll('[data-symbol-input]').forEach((input) => input.addEventListener('change', updateSymbol));
  root.querySelectorAll('[data-symbol-input]').forEach((input) => {
    const iconTarget = input.closest('.symbol-choice')?.querySelector('[data-symbol-option-icon]');
    if (!iconTarget) return;
    iconTarget.innerHTML = input.value === 'Ingen' ? '—' : (vectorizer?.iconMarkup(input.value) || '');
  });

  root.querySelectorAll('[data-variant-button]').forEach((button) => {
    button.addEventListener('click', () => {
      invalidateProductionFile();
      root.querySelectorAll('[data-variant-button]').forEach((item) => item.classList.remove('is-selected'));
      button.classList.add('is-selected');
      if (variantInput) variantInput.value = button.dataset.variantId;
      if (widthProperty) widthProperty.value = `${button.dataset.width} cm`;
      if (widthMm) widthMm.textContent = String(Number(button.dataset.width) * 10);
      updatePrice();
      updateMeasurements();
      updateSummary();
      window.requestAnimationFrame(fitPreview);
    });
  });

  root.querySelectorAll('[data-surface-button]').forEach((button) => {
    button.addEventListener('click', () => {
      root.querySelectorAll('[data-surface-button]').forEach((item) => {
        const active = item === button;
        item.classList.toggle('is-selected', active);
        item.setAttribute('aria-pressed', String(active));
      });
      if (preview) preview.dataset.surface = button.dataset.surface || 'front-window';
    });
  });

  root.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
    if (!quantityInput) return;
    quantityInput.value = String(Math.max(1, Number(quantityInput.value || 1) - 1));
    updatePrice();
  });
  root.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
    if (!quantityInput) return;
    quantityInput.value = String(Math.min(99, Number(quantityInput.value || 1) + 1));
    updatePrice();
  });
  quantityInput?.addEventListener('input', updatePrice);
  mirrorInput?.addEventListener('change', () => {
    invalidateProductionFile();
    if (preview) preview.classList.toggle('is-mirror-order', mirrorInput.checked);
    updateSummary();
  });

  form?.addEventListener('submit', async (event) => {
    if (productionReady) return;
    event.preventDefault();
    if (!form.reportValidity()) return;

    if (!vectorizer || !productionFileInput || typeof DataTransfer === 'undefined') {
      if (productionError) {
        productionError.textContent = 'Din webbläsare kunde inte skapa produktionsfilen. Uppdatera sidan eller prova Safari/Chrome igen.';
        productionError.hidden = false;
      }
      return;
    }

    submitButton?.setAttribute('aria-busy', 'true');
    if (submitButton) submitButton.disabled = true;
    if (submitLabel) submitLabel.textContent = 'FÖRBEREDER DIN DEKAL…';
    if (productionError) productionError.hidden = true;

    try {
      updateSummary();
      const font = selectedFont();
      const color = selectedColor();
      const symbol = selectedSymbol();
      const variant = selectedVariant();
      const result = await vectorizer.generateProductionFile({
        text: textInput?.value,
        fontUrl: font?.dataset.fontUrl,
        fontName: font?.value,
        color: color?.dataset.color,
        colorName: color?.value,
        widthMm: Number(variant?.dataset.width || 10) * 10,
        symbol: symbol?.value === 'Ingen' ? null : symbol?.value,
        mirror: Boolean(mirrorInput?.checked),
        specification: specificationProperty?.value,
      });
      const transfer = new DataTransfer();
      transfer.items.add(result.file);
      productionFileInput.files = transfer.files;
      if (productionFileInput.files.length !== 1) throw new Error('Produktionsfilen kunde inte kopplas till ordern.');

      const heightText = result.heightMm.toLocaleString('sv-SE', { maximumFractionDigits: 1 });
      if (heightMm) heightMm.textContent = heightText;
      if (productionMeasureProperty) productionMeasureProperty.value = `${result.widthMm} × ${heightText} mm`;
      if (designIdProperty) designIdProperty.value = `SD-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
      productionReady = true;
      form.submit();
    } catch (error) {
      if (productionError) {
        productionError.textContent = error?.message || 'Produktionsfilen kunde inte skapas. Försök igen.';
        productionError.hidden = false;
      }
      submitButton?.removeAttribute('aria-busy');
      if (submitButton) submitButton.disabled = false;
      if (submitLabel) submitLabel.textContent = 'LÄGG I VARUKORG';
    }
  });

  updateText();
  updateFont();
  updateSymbol();
  updatePrice();
  updateSummary();
  document.fonts?.ready.then(() => {
    fitPreview();
    alignPreviewSymbol();
  });
  window.addEventListener('resize', fitPreview, { passive: true });
}

function initDesigner(root) {
  const form = root.querySelector('form');
  const stage = root.querySelector('[data-designer-stage]');
  const mobilePreviewOpen = root.querySelector('[data-mobile-preview-open]');
  const mobilePreviewCloseButtons = root.querySelectorAll('[data-mobile-preview-close]');
  const previewBackdrop = root.querySelector('.designer-preview-backdrop');
  const mobileSubmit = root.querySelector('[data-mobile-submit]');
  const mobileSubmitLabel = root.querySelector('[data-mobile-submit-label]');
  const resetDesignButton = root.querySelector('[data-reset-design]');
  const textProperty = root.querySelector('[data-text-property]');
  const lineCountProperty = root.querySelector('[data-line-count-property]');
  const fontProperty = root.querySelector('[data-font-property]');
  const lineSettingsProperty = root.querySelector('[data-line-settings-property]');
  const previewText = root.querySelector('[data-preview-text]');
  const previewArt = root.querySelector('[data-preview-art]');
  const symbolLayer = root.querySelector('[data-symbol-layer]');
  const symbolToolbar = root.querySelector('[data-symbol-toolbar]');
  const selectedSymbolName = root.querySelector('[data-selected-symbol-name]');
  const symbolSnapStatus = root.querySelector('[data-symbol-snap-status]');
  const symbolProperty = root.querySelector('[data-symbol-property]');
  const layoutProperty = root.querySelector('[data-layout-property]');
  const symbolCount = root.querySelector('[data-symbol-count]');
  const symbolLimit = root.querySelector('[data-symbol-limit]');
  const symbolJump = root.querySelector('[data-symbol-jump]');
  const symbolClearAll = root.querySelector('[data-symbol-clear-all]');
  const variantInput = root.querySelector('[data-variant-input]');
  const widthProperty = root.querySelector('[data-width-property]');
  const specificationProperty = root.querySelector('[data-specification-property]');
  const productionMeasureProperty = root.querySelector('[data-production-measure-property]');
  const productionFileInput = root.querySelector('[data-production-file]');
  const designIdProperty = root.querySelector('[data-design-id-property]');
  const productionError = root.querySelector('[data-production-error]');
  const submitButton = root.querySelector('[data-designer-submit]');
  const submitLabel = root.querySelector('[data-submit-label]');
  const widthMm = root.querySelector('[data-width-mm]');
  const heightMm = root.querySelector('[data-height-mm]');
  const priceTargets = root.querySelectorAll('[data-product-price], [data-summary-price], [data-sticky-price]');
  const unitPrice = root.querySelector('[data-unit-price]');
  const quantityInput = root.querySelector('[data-quantity-input]');
  const summaryText = root.querySelector('[data-summary-text]');
  const summaryFont = root.querySelector('[data-summary-font]');
  const summaryColor = root.querySelector('[data-summary-color]');
  const summarySymbols = root.querySelector('[data-summary-symbols]');
  const summaryMounting = root.querySelector('[data-summary-mounting]');
  const measureTextTargets = root.querySelectorAll('[data-summary-measure], [data-mobile-measure]');
  const preview = root.querySelector('[data-preview]');
  const previewScaleInput = root.querySelector('[data-preview-scale]');
  const previewScaleOutput = root.querySelector('[data-preview-scale-output]');
  const mirrorInput = root.querySelector('[data-mirror-input]');
  const vectorizer = window.SwedeDecalVectorizer;
  const previewMeasureContext = (() => {
    try {
      return document.createElement('canvas').getContext('2d');
    } catch (_) {
      return null;
    }
  })();
  const MAX_SYMBOLS = 3;
  const symbolItems = [];
  const firstTextInput = root.querySelector('[data-text-input]');
  const firstFontInput = root.querySelector('[data-font-input]:checked');
  const textLines = [{
    id: '1',
    text: firstTextInput?.value || 'DIN TEXT',
    fontName: firstFontInput?.value || 'Josefin Sans',
    fontFamily: firstFontInput?.dataset.font || firstFontInput?.value || 'Josefin Sans',
    fontLabel: firstFontInput?.dataset.fontLabel || 'Ren',
    fontUrl: firstFontInput?.dataset.fontUrl || '',
    fontCode: firstFontInput?.dataset.code || 'F50',
    scale: 1,
  }];
  let activeTextLineId = '1';
  let currentVariantRecord = null;
  let activeSymbolId = null;
  let symbolSnapMessage = '';
  let symbolSequence = 0;
  let productionReady = false;
  let fitFrame = 0;
  let limitTimer = 0;
  let layoutOffsetX = 0;
  let layoutOffsetY = 0;

  if (form) {
    form.enctype = 'multipart/form-data';
    form.encoding = 'multipart/form-data';
  }

  const selectedFont = () => root.querySelector('[data-font-input]:checked');
  const selectedColor = () => root.querySelector('[data-color-input]:checked');
  const selectedVariant = () => currentVariantRecord;
  const selectedWidthButton = () => root.querySelector('[data-variant-button].is-selected');
  const activeTextLine = () => textLines.find((line) => line.id === activeTextLineId) || textLines[0];
  const selectedWidthCm = () => Number(selectedWidthButton()?.dataset.width || 30);
  const variantRecordFor = (width) => root.querySelector(`[data-variant-record][data-width="${width}"][data-lines="1"]`);
  const activeSymbol = () => symbolItems.find((item) => item.id === activeSymbolId) || null;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const rounded = (value) => Math.round(Number(value) * 1000) / 1000;
  const formatSek = (cents) => new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK', maximumFractionDigits: 0 }).format(cents / 100);
  const initialState = {
    lines: textLines.map((line) => ({ ...line })),
    colorInput: selectedColor(),
    variantButton: selectedWidthButton(),
    surface: preview?.dataset.surface || 'front-window',
  };
  const surfaceDefaults = Object.freeze({
    'front-window': { x: 0.5, y: 0.67, scale: 0.75 },
    'rear-window': { x: 0.5, y: 0.27, scale: 0.75 },
    'truck-window': { x: 0.5, y: 0.38, scale: 0.75 },
    'motorhome-rear': { x: 0.5, y: 0.24, scale: 0.75 },
    mailbox: { x: 0.5, y: 0.52, scale: 0.75 },
    'child-door': { x: 0.5, y: 0.4, scale: 0.75 },
  });
  const surfacePlacements = Object.fromEntries(
    Object.entries(surfaceDefaults).map(([surface, placement]) => [surface, { ...placement, custom: false }]),
  );
  let currentSurface = initialState.surface;

  const currentPlacement = () => {
    const fallback = surfaceDefaults[currentSurface] || { x: 0.5, y: 0.5, scale: 0.75 };
    return surfacePlacements[currentSurface] || (surfacePlacements[currentSurface] = { ...fallback, custom: false });
  };
  const currentPreviewScale = () => clamp(Number(currentPlacement().scale) || 0.75, 0.35, 1.1);

  const invalidateProductionFile = () => {
    productionReady = false;
    if (productionFileInput?.files?.length) productionFileInput.value = '';
    if (productionError) productionError.hidden = true;
  };

  const lineScaleLabel = (scale) => ({ 0.8: 'Liten', 1: 'Normal', 1.2: 'Stor' }[Number(scale)] || `${Math.round(Number(scale) * 100)}%`);

  const syncActiveTextLineUi = () => {
    const line = activeTextLine();
    root.querySelectorAll('[data-font-input]').forEach((input) => { input.checked = input.value === line?.fontName; });
    const input = selectedFont();
    root.querySelectorAll('.font-choice').forEach((choice) => choice.classList.toggle('is-selected', choice.contains(input)));
    updateSelectedFont();
  };

  const renderPreviewLines = () => {
    if (!previewText) return;
    previewText.replaceChildren();
    textLines.forEach((line, index) => {
      const element = document.createElement('span');
      element.className = 'designer-preview-line';
      element.dataset.previewLine = line.id;
      element.textContent = line.text.trim() || `RAD ${index + 1}`;
      element.style.fontFamily = line.fontFamily;
      element.style.fontSize = `${line.scale}em`;
      previewText.append(element);
    });
  };

  const getSymbolMetrics = (item) => vectorizer?.symbolMetrics?.(item?.name) || { aspect: 1 };

  const symbolOpticalOffset = () => {
    const line = activeTextLine();
    const lineElement = previewText?.querySelector('[data-preview-line]');
    const style = getComputedStyle(lineElement || previewArt);
    const parentFontSize = Number.parseFloat(getComputedStyle(previewArt).fontSize || '0') || 60;
    const fontSize = String(style.fontSize || '').endsWith('px')
      ? Number.parseFloat(style.fontSize) || parentFontSize
      : parentFontSize * (Number(line?.scale) || 1);
    let offset = -0.08 * fontSize;

    if (previewMeasureContext && line) {
      previewMeasureContext.font = `${style.fontStyle || 'normal'} ${style.fontWeight || '400'} ${fontSize}px ${style.fontFamily || line.fontFamily}`;
      const metrics = previewMeasureContext.measureText(line.text.trim() || 'DIN TEXT');
      const values = [metrics.fontBoundingBoxAscent, metrics.fontBoundingBoxDescent, metrics.actualBoundingBoxAscent, metrics.actualBoundingBoxDescent];
      if (values.every(Number.isFinite)) {
        offset = (metrics.fontBoundingBoxAscent - metrics.fontBoundingBoxDescent + metrics.actualBoundingBoxDescent - metrics.actualBoundingBoxAscent) / 2;
      }
    }

    return rounded(clamp(offset, -fontSize * 0.22, fontSize * 0.12));
  };

  const syncSymbolOpticalAlignment = () => {
    previewArt?.style.setProperty('--symbol-optical-offset', `${symbolOpticalOffset().toFixed(2)}px`);
  };

  const dockedSymbolPosition = (item, side, textRect = previewText?.getBoundingClientRect()) => {
    if (!item || !textRect?.width) return null;
    const visualScale = currentPreviewScale();
    const textWidth = Math.max(1, textRect.width / visualScale);
    const fontSize = Number.parseFloat(getComputedStyle(previewArt).fontSize || '0') || 60;
    const iconWidth = fontSize * item.scale * (getSymbolMetrics(item).aspect || 1);
    const gap = clamp(fontSize * 0.08, 4, 8);
    const edgeOffset = (gap + iconWidth / 2) / textWidth;
    return { x: rounded(side === 'before' ? -edgeOffset : 1 + edgeOffset), y: 0.5 };
  };

  const setSymbolSnapFeedback = (message = '', showGuide = false) => {
    symbolSnapMessage = message;
    preview?.classList.toggle('is-symbol-center-snapped', Boolean(showGuide));
    if (symbolSnapStatus) symbolSnapStatus.textContent = message || 'Placeringen sparas automatiskt';
  };

  const previewLayoutBounds = () => {
    if (!previewText || !previewArt) return null;
    const textRect = previewText.getBoundingClientRect();
    const visualScale = currentPreviewScale();
    const fontSize = Number.parseFloat(getComputedStyle(previewArt).fontSize || '0') || 60;
    const textWidth = Math.max(1, textRect.width / visualScale);
    const textHeight = Math.max(1, textRect.height / visualScale);
    let minX = 0;
    let minY = 0;
    let maxX = textWidth;
    let maxY = textHeight;

    symbolItems.forEach((item) => {
      const aspect = getSymbolMetrics(item).aspect || 1;
      const itemHeight = fontSize * item.scale;
      const itemWidth = itemHeight * aspect;
      const centerX = item.x * textWidth;
      const centerY = item.y * textHeight + symbolOpticalOffset();
      minX = Math.min(minX, centerX - itemWidth / 2);
      minY = Math.min(minY, centerY - itemHeight / 2);
      maxX = Math.max(maxX, centerX + itemWidth / 2);
      maxY = Math.max(maxY, centerY + itemHeight / 2);
    });

    return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY, textWidth, textHeight };
  };

  const applyPreviewTransform = () => {
    if (!previewArt) return;
    previewArt.style.transform = `translate(calc(-50% + ${layoutOffsetX.toFixed(2)}px),calc(-50% + ${layoutOffsetY.toFixed(2)}px)) scale(${currentPreviewScale().toFixed(2)})`;
  };

  const syncPreviewScaleControl = () => {
    const percent = Math.round(currentPreviewScale() * 100);
    if (previewScaleInput) {
      previewScaleInput.value = String(percent);
      previewScaleInput.setAttribute('aria-valuetext', `${percent} procent av visningsstorleken`);
    }
    if (previewScaleOutput) previewScaleOutput.textContent = `${percent}%`;
  };

  const setPreviewScale = (percent) => {
    const placement = currentPlacement();
    placement.scale = rounded(clamp(Number(percent) / 100, 0.35, 1.1));
    syncPreviewScaleControl();
    applyPreviewTransform();
    applySurfacePlacement();
  };

  const placementLimits = () => {
    const previewRect = preview?.getBoundingClientRect();
    const bounds = previewLayoutBounds();
    if (!previewRect?.width || !previewRect?.height || !bounds) return { minX: 0.08, maxX: 0.92, minY: 0.1, maxY: 0.76 };

    const edge = 12;
    const safeBottom = previewRect.height - edge;
    const visualScale = currentPreviewScale();
    const halfWidth = Math.min((bounds.width * visualScale) / 2, Math.max(0, previewRect.width / 2 - edge));
    const halfHeight = Math.min((bounds.height * visualScale) / 2, Math.max(0, safeBottom / 2 - edge));
    let minX = (edge + halfWidth) / previewRect.width;
    let maxX = (previewRect.width - edge - halfWidth) / previewRect.width;
    let minY = (edge + halfHeight) / previewRect.height;
    let maxY = (safeBottom - halfHeight) / previewRect.height;

    if (minX > maxX) minX = maxX = 0.5;
    if (minY > maxY) minY = maxY = Math.max(0.3, safeBottom / previewRect.height / 2);
    return { minX, maxX, minY, maxY };
  };

  const applySurfacePlacement = ({ markCustom = false } = {}) => {
    if (!previewArt) return;
    const placement = currentPlacement();
    const limits = placementLimits();
    placement.x = rounded(clamp(placement.x, limits.minX, limits.maxX));
    placement.y = rounded(clamp(placement.y, limits.minY, limits.maxY));
    if (markCustom) placement.custom = true;
    previewArt.style.left = `${(placement.x * 100).toFixed(3)}%`;
    previewArt.style.top = `${(placement.y * 100).toFixed(3)}%`;
    preview?.classList.toggle('has-custom-placement', Boolean(placement.custom));
  };

  const resetSurfacePlacements = () => {
    Object.entries(surfaceDefaults).forEach(([surface, placement]) => {
      surfacePlacements[surface] = { ...placement, custom: false };
    });
  };

  const fitPreview = () => {
    if (!previewArt?.parentElement || !previewText) return;
    const maxFontSize = window.matchMedia('(max-width: 560px)').matches ? 58 : 86;
    const horizontalRoom = Math.max(190, previewArt.parentElement.clientWidth - (window.innerWidth <= 560 ? 38 : 90));
    const verticalRoom = Math.max(110, previewArt.parentElement.clientHeight - (symbolItems.length ? 245 : 190));
    layoutOffsetX = 0;
    layoutOffsetY = 0;
    applyPreviewTransform();
    previewArt.style.fontSize = `${maxFontSize}px`;
    syncSymbolOpticalAlignment();

    const initial = previewLayoutBounds();
    if (!initial) return;
    const fitScale = Math.min(1, horizontalRoom / Math.max(1, initial.width), verticalRoom / Math.max(1, initial.height));
    previewArt.style.fontSize = `${Math.max(18, maxFontSize * fitScale).toFixed(2)}px`;
    syncSymbolOpticalAlignment();

    const fitted = previewLayoutBounds();
    if (!fitted) return;
    const offsetX = fitted.textWidth / 2 - (fitted.minX + fitted.maxX) / 2;
    const offsetY = fitted.textHeight / 2 - (fitted.minY + fitted.maxY) / 2;
    layoutOffsetX = offsetX;
    layoutOffsetY = offsetY;
    applyPreviewTransform();
    applySurfacePlacement();
  };

  const scheduleFit = () => {
    window.cancelAnimationFrame(fitFrame);
    fitFrame = window.requestAnimationFrame(() => {
      fitPreview();
      updateMeasurements();
    });
  };

  const layoutPayload = () => ({
    version: 3,
    lines: textLines.map((line) => ({
      text: line.text.trim(),
      font: line.fontName,
      fontCode: line.fontCode,
      scale: rounded(line.scale),
    })),
    symbols: symbolItems.map((item) => ({
      name: item.name,
      x: rounded(item.x),
      y: rounded(item.y),
      scale: rounded(item.scale),
      dock: item.dock || null,
    })),
  });

  const updateSymbolFields = () => {
    const names = symbolItems.map((item) => item.name);
    if (symbolProperty) symbolProperty.value = names.length ? names.join(', ') : 'Ingen';
    if (layoutProperty) layoutProperty.value = JSON.stringify(layoutPayload());
    if (symbolCount) symbolCount.textContent = String(symbolItems.length);
    if (symbolJump) symbolJump.disabled = symbolItems.length === 0;
    if (symbolClearAll) symbolClearAll.disabled = symbolItems.length === 0;
    const clearButton = root.querySelector('[data-symbol-clear]');
    clearButton?.classList.toggle('is-selected', symbolItems.length === 0);
    clearButton?.setAttribute('aria-pressed', String(symbolItems.length === 0));
    root.querySelectorAll('[data-symbol-add]').forEach((button) => {
      const selected = activeSymbol()?.name === button.dataset.symbol;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
      button.setAttribute('aria-disabled', String(symbolItems.length >= MAX_SYMBOLS));
    });
    preview?.classList.toggle('has-symbols', symbolItems.length > 0);
    preview?.classList.toggle('has-active-symbol', Boolean(activeSymbol()));
  };

  const updateToolbar = () => {
    const item = activeSymbol();
    if (symbolToolbar) symbolToolbar.hidden = !item;
    if (selectedSymbolName) selectedSymbolName.textContent = item?.name || '';
    if (symbolSnapStatus) symbolSnapStatus.textContent = symbolSnapMessage || 'Placeringen sparas automatiskt';
    symbolLayer?.querySelectorAll('[data-symbol-object]').forEach((element) => {
      const selected = element.dataset.symbolId === activeSymbolId;
      element.classList.toggle('is-selected', selected);
      element.setAttribute('aria-pressed', String(selected));
    });
    updateSymbolFields();
  };

  function updateMeasurements() {
    scheduleProductionCheck();
  }

  function updateSummary() {
    const color = selectedColor();
    const variant = selectedVariant();
    const texts = textLines.map((line, index) => line.text.trim() || `RAD ${index + 1}`);
    const text = texts.join(' / ');
    const width = variant?.dataset.width ? `${variant.dataset.width} cm` : '';
    const uniqueFonts = [...new Set(textLines.map((line) => line.fontName))];
    const symbolText = symbolItems.length
      ? `${symbolItems.length} symbol${symbolItems.length === 1 ? '' : 'er'}: ${symbolItems.map((item) => item.name).join(' + ')}`
      : 'Inga symboler';
    if (summaryText) summaryText.textContent = text;
    if (summaryFont) summaryFont.textContent = uniqueFonts.length === 1 ? uniqueFonts[0] : 'Blandade typsnitt';
    if (summaryColor) summaryColor.textContent = color?.value || '—';
    if (summarySymbols) summarySymbols.textContent = symbolText;
    if (summaryMounting) summaryMounting.textContent = mirrorInput?.checked ? 'Insida glas · spegelvänd' : 'Utsida / slät yta';
    if (textProperty) textProperty.value = texts.join('\n');
    if (lineCountProperty) lineCountProperty.value = `${textLines.length} ${textLines.length === 1 ? 'rad' : 'rader'}`;
    if (fontProperty) fontProperty.value = uniqueFonts.length === 1 ? uniqueFonts[0] : textLines.map((line, index) => `Rad ${index + 1}: ${line.fontName}`).join(' · ');
    if (lineSettingsProperty) lineSettingsProperty.value = textLines.map((line, index) => `Rad ${index + 1}: ${line.fontName} · ${lineScaleLabel(line.scale)}`).join(' | ');
    if (specificationProperty) {
      const symbols = symbolItems.length ? symbolItems.map((item) => item.name.toUpperCase()).join('+') : 'NO-SYMBOL';
      const lineSpec = textLines.map((line) => `${line.fontCode}@${Math.round(line.scale * 100)}`).join('+');
      specificationProperty.value = [`R${textLines.length}`, lineSpec, color?.dataset.code, width, 'FREEFORM-V3', symbols, mirrorInput?.checked ? 'MIRROR' : 'NORMAL'].filter(Boolean).join('-');
    }
    scheduleProductionCheck();
  }

  const syncSymbolState = ({ refit = true } = {}) => {
    invalidateProductionFile();
    updateToolbar();
    updateSummary();
    updateMeasurements();
    if (refit) scheduleFit();
  };

  const selectSymbol = (id) => {
    const nextId = symbolItems.some((item) => item.id === id) ? id : null;
    if (nextId !== activeSymbolId) setSymbolSnapFeedback();
    activeSymbolId = nextId;
    updateToolbar();
  };

  const finishSymbolEditing = () => {
    symbolLayer?.querySelector(`[data-symbol-id="${activeSymbolId}"]`)?.blur();
    activeSymbolId = null;
    setSymbolSnapFeedback();
    updateToolbar();
  };

  const updateSymbolElement = (element, item) => {
    const metrics = getSymbolMetrics(item);
    const aspect = metrics.aspect || 1;
    element.style.left = `${(item.x * 100).toFixed(3)}%`;
    element.style.top = `${(item.y * 100).toFixed(3)}%`;
    element.style.width = `${rounded(item.scale * aspect)}em`;
    element.style.height = `${rounded(item.scale)}em`;
  };

  const nudgeSymbol = (item, deltaX, deltaY) => {
    item.x = rounded(clamp(item.x + deltaX, -1.25, 2.25));
    item.y = rounded(clamp(item.y + deltaY, -1.4, 2.4));
    item.dock = null;
    if (Math.abs(item.y - 0.5) <= 0.04) item.y = 0.5;
    setSymbolSnapFeedback(item.y === 0.5 ? '✓ CENTRERAD MED TEXTEN' : '');
    const element = symbolLayer?.querySelector(`[data-symbol-id="${item.id}"]`);
    if (element) updateSymbolElement(element, item);
    syncSymbolState();
  };

  const beginSymbolDrag = (event, item, element) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.preventDefault();
    selectSymbol(item.id);
    element.setPointerCapture?.(event.pointerId);
    element.classList.add('is-dragging');

    const move = (moveEvent) => {
      const textRect = previewText?.getBoundingClientRect();
      if (!textRect?.width || !textRect?.height) return;
      let x = (moveEvent.clientX - textRect.left) / textRect.width;
      const opticalOffset = symbolOpticalOffset() * currentPreviewScale();
      let y = (moveEvent.clientY - textRect.top - opticalOffset) / textRect.height;
      const centerThreshold = clamp(14 / textRect.height, 0.12, 0.35);
      const dockThreshold = clamp(16 / textRect.width, 0.06, 0.25);
      const before = dockedSymbolPosition(item, 'before', textRect);
      const after = dockedSymbolPosition(item, 'after', textRect);
      const closeToCenter = Math.abs(y - 0.5) <= centerThreshold;
      let snapMessage = '';
      item.dock = null;

      if (closeToCenter && before && Math.abs(x - before.x) <= dockThreshold) {
        x = before.x;
        y = 0.5;
        item.dock = 'before';
        snapMessage = '✓ FÄST FÖRE TEXTEN';
      } else if (closeToCenter && after && Math.abs(x - after.x) <= dockThreshold) {
        x = after.x;
        y = 0.5;
        item.dock = 'after';
        snapMessage = '✓ FÄST EFTER TEXTEN';
      } else if (closeToCenter) {
        y = 0.5;
        snapMessage = '✓ CENTRERAD MED TEXTEN';
      }

      setSymbolSnapFeedback(snapMessage, Boolean(snapMessage));
      item.x = rounded(clamp(x, -1.25, 2.25));
      item.y = rounded(clamp(y, -1.4, 2.4));
      updateSymbolElement(element, item);
      syncSymbolState({ refit: false });
    };

    const finish = (upEvent) => {
      element.classList.remove('is-dragging');
      setSymbolSnapFeedback(symbolSnapMessage, false);
      element.releasePointerCapture?.(upEvent.pointerId);
      element.removeEventListener('pointermove', move);
      element.removeEventListener('pointerup', finish);
      element.removeEventListener('pointercancel', finish);
      scheduleFit();
    };

    element.addEventListener('pointermove', move);
    element.addEventListener('pointerup', finish);
    element.addEventListener('pointercancel', finish);
  };

  const renderSymbols = () => {
    if (!symbolLayer) return;
    symbolLayer.replaceChildren();
    symbolItems.forEach((item) => {
      const element = document.createElement('button');
      element.type = 'button';
      element.className = 'designer-symbol-object';
      element.dataset.symbolObject = '';
      element.dataset.symbolId = item.id;
      element.setAttribute('aria-label', `${item.name}. Dra för att flytta. Använd piltangenter för finjustering.`);
      element.innerHTML = vectorizer?.iconMarkup(item.name) || '';
      updateSymbolElement(element, item);
      element.addEventListener('click', () => selectSymbol(item.id));
      element.addEventListener('pointerdown', (event) => beginSymbolDrag(event, item, element));
      element.addEventListener('keydown', (event) => {
        const amount = event.shiftKey ? 0.1 : 0.025;
        const moves = { ArrowLeft: [-amount, 0], ArrowRight: [amount, 0], ArrowUp: [0, -amount], ArrowDown: [0, amount] };
        if (moves[event.key]) {
          event.preventDefault();
          nudgeSymbol(item, ...moves[event.key]);
        }
        if (event.key === 'Delete' || event.key === 'Backspace') {
          event.preventDefault();
          removeActiveSymbol();
        }
      });
      symbolLayer.append(element);
    });
    updateToolbar();
    scheduleFit();
  };

  const showSymbolLimit = () => {
    if (!symbolLimit) return;
    window.clearTimeout(limitTimer);
    symbolLimit.hidden = false;
    limitTimer = window.setTimeout(() => { symbolLimit.hidden = true; }, 3500);
  };

  const addSymbol = (name) => {
    if (!name || !vectorizer?.hasSymbol?.(name)) return;
    if (symbolItems.length >= MAX_SYMBOLS) {
      showSymbolLimit();
      return;
    }
    const defaults = [
      { x: 1.05, y: 0.5, scale: 0.88, dock: 'after' },
      { x: -0.05, y: 0.5, scale: 0.88, dock: 'before' },
      { x: 0.5, y: -0.55, scale: 0.66, dock: null },
    ];
    const item = { id: `SD-SYMBOL-${++symbolSequence}`, name, ...defaults[symbolItems.length] };
    if (item.dock) {
      const position = dockedSymbolPosition(item, item.dock);
      if (position) Object.assign(item, position);
    }
    symbolItems.push(item);
    activeSymbolId = item.id;
    if (symbolLimit) symbolLimit.hidden = true;
    renderSymbols();
    syncSymbolState();
  };

  function removeActiveSymbol() {
    const index = symbolItems.findIndex((item) => item.id === activeSymbolId);
    if (index < 0) return;
    symbolItems.splice(index, 1);
    activeSymbolId = null;
    setSymbolSnapFeedback();
    renderSymbols();
    syncSymbolState();
  }

  const clearSymbols = () => {
    symbolItems.splice(0);
    activeSymbolId = null;
    setSymbolSnapFeedback();
    if (symbolLimit) symbolLimit.hidden = true;
    renderSymbols();
    syncSymbolState();
  };

  const dockActiveSymbol = (side) => {
    const item = activeSymbol();
    const position = dockedSymbolPosition(item, side);
    if (!item || !position) return;
    item.x = position.x;
    item.y = position.y;
    item.dock = side;
    setSymbolSnapFeedback(side === 'before' ? '✓ EXAKT FÖRE TEXTEN' : '✓ EXAKT EFTER TEXTEN');
    const element = symbolLayer?.querySelector(`[data-symbol-id="${item.id}"]`);
    if (element) updateSymbolElement(element, item);
    syncSymbolState();
  };

  const resizeActiveSymbol = (delta) => {
    const item = activeSymbol();
    if (!item) return;
    item.scale = rounded(clamp(item.scale + delta, 0.35, 1.6));
    if (item.dock) {
      const position = dockedSymbolPosition(item, item.dock);
      if (position) Object.assign(item, position);
    }
    const element = symbolLayer?.querySelector(`[data-symbol-id="${item.id}"]`);
    if (element) updateSymbolElement(element, item);
    syncSymbolState();
  };

  const updateText = () => {
    invalidateProductionFile();
    updateFontSamples();
    renderPreviewLines();
    updateSummary();
    scheduleFit();
  };

  const updateFont = () => {
    invalidateProductionFile();
    const input = selectedFont();
    const line = activeTextLine();
    if (line && input) {
      line.fontName = input.value;
      line.fontFamily = input.dataset.font || input.value;
      line.fontLabel = input.dataset.fontLabel || '';
      line.fontUrl = input.dataset.fontUrl || '';
      line.fontCode = input.dataset.code || '';
    }
    syncActiveTextLineUi();
    renderPreviewLines();
    updateSummary();
    scheduleFit();
    if (input && document.fonts?.load) document.fonts.load('32px "' + input.dataset.font + '"').then(scheduleFit).catch(() => {});
  };

  const updatePrice = () => {
    const variant = selectedVariant();
    const cents = Number(variant?.dataset.priceCents || 0);
    const quantity = Math.max(1, Number.parseInt(quantityInput?.value || '1', 10) || 1);
    if (quantityInput) quantityInput.value = String(Math.min(99, quantity));
    const totalPrice = cents ? formatSek(cents * Math.min(99, quantity)) : moneyText(variant?.dataset.price);
    priceTargets.forEach((target) => { target.textContent = totalPrice; });
    if (unitPrice) unitPrice.textContent = `${moneyText(variant?.dataset.price)} / st`;
  };

  const syncWidthButtons = () => {
    root.querySelectorAll('[data-variant-button]').forEach((button) => {
      const width = Number(button.dataset.width);
      const record = variantRecordFor(width);
      const unavailable = !record || record.dataset.available === 'false';
      button.disabled = unavailable;
      button.setAttribute('aria-disabled', String(unavailable));
      const price = button.querySelector('[data-size-button-price]');
      if (price) price.textContent = moneyText(record?.dataset.price);
    });
  };

  const syncVariantForDesign = () => {
    const width = selectedWidthCm();
    const record = variantRecordFor(width);
    syncWidthButtons();
    if (width < 30 || !record || record.dataset.available === 'false') return false;
    currentVariantRecord = record;
    if (variantInput) variantInput.value = record.dataset.variantId;
    if (widthProperty) widthProperty.value = `${width} cm`;
    if (widthMm) widthMm.textContent = String(width * 10);
    updatePrice();
    updateSummary();
    scheduleFit();
    return true;
  };

  const selectVariantButton = (button) => {
    if (!button || button.disabled) return;
    invalidateProductionFile();
    root.querySelectorAll('[data-variant-button]').forEach((item) => item.classList.toggle('is-selected', item === button));
    syncVariantForDesign();
  };

  const selectSurfaceButton = (button) => {
    if (!button) return;
    root.querySelectorAll('[data-surface-button]').forEach((item) => {
      const active = item === button;
      item.classList.toggle('is-selected', active);
      item.setAttribute('aria-pressed', String(active));
    });
    currentSurface = button.dataset.surface || 'front-window';
    if (preview) preview.dataset.surface = currentSurface;
    syncPreviewScaleControl();
    applyPreviewTransform();
    applySurfacePlacement();
    scheduleFit();
  };

  const nudgePlacement = (deltaX, deltaY) => {
    const placement = currentPlacement();
    placement.x += deltaX;
    placement.y += deltaY;
    applySurfacePlacement({ markCustom: true });
  };

  const beginPlacementDrag = (event) => {
    if (!preview || !previewText || (event.pointerType === 'mouse' && event.button !== 0)) return;
    event.preventDefault();
    const placement = currentPlacement();
    const startX = event.clientX;
    const startY = event.clientY;
    const originX = placement.x;
    const originY = placement.y;
    previewText.setPointerCapture?.(event.pointerId);
    preview.classList.add('is-dragging-placement');

    const move = (moveEvent) => {
      const previewRect = preview.getBoundingClientRect();
      if (!previewRect.width || !previewRect.height) return;
      placement.x = originX + (moveEvent.clientX - startX) / previewRect.width;
      placement.y = originY + (moveEvent.clientY - startY) / previewRect.height;
      applySurfacePlacement({ markCustom: true });
    };

    const finish = (upEvent) => {
      preview.classList.remove('is-dragging-placement');
      previewText.releasePointerCapture?.(upEvent.pointerId);
      previewText.removeEventListener('pointermove', move);
      previewText.removeEventListener('pointerup', finish);
      previewText.removeEventListener('pointercancel', finish);
    };

    previewText.addEventListener('pointermove', move);
    previewText.addEventListener('pointerup', finish);
    previewText.addEventListener('pointercancel', finish);
  };

  const selectSymbolCategory = (category) => {
    root.querySelectorAll('[data-symbol-category-button]').forEach((button) => {
      const active = button.dataset.symbolCategoryButton === category;
      button.classList.toggle('is-selected', active);
      button.setAttribute('aria-selected', String(active));
    });
    root.querySelectorAll('[data-symbol-category-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.symbolCategoryPanel !== category;
    });
  };

  const setMobilePreview = (open, { restoreFocus = false } = {}) => {
    stage?.classList.toggle('is-mobile-open', open);
    if (previewBackdrop) previewBackdrop.hidden = !open;
    mobilePreviewOpen?.setAttribute('aria-expanded', String(open));
    document.documentElement.classList.toggle('designer-preview-open', open);
    if (stage) {
      if (open) {
        stage.setAttribute('role', 'dialog');
        stage.setAttribute('aria-modal', 'true');
        stage.setAttribute('aria-label', 'Din liveförhandsvisning');
      } else {
        stage.removeAttribute('role');
        stage.removeAttribute('aria-modal');
        stage.removeAttribute('aria-label');
      }
    }
    if (open) {
      window.setTimeout(() => {
        stage?.querySelector('.designer-mobile-preview-close')?.focus();
        scheduleFit();
      }, 20);
    } else if (restoreFocus) {
      mobilePreviewOpen?.focus();
    }
  };

  const resetDesign = () => {
    textLines.splice(0, textLines.length, ...initialState.lines.map((line) => ({ ...line })));
    activeTextLineId = textLines[0].id;
    if (firstTextInput) firstTextInput.value = textLines[0].text;
    const characterCount = root.querySelector('[data-character-count]');
    if (characterCount) characterCount.textContent = String(textLines[0].text.length);
    syncActiveTextLineUi();
    root.querySelectorAll('[data-color-input]').forEach((input) => { input.checked = input === initialState.colorInput; });
    if (previewArt && initialState.colorInput) previewArt.style.color = initialState.colorInput.dataset.color;
    if (quantityInput) quantityInput.value = '1';
    if (mirrorInput) mirrorInput.checked = false;
    preview?.classList.remove('is-mirror-order');
    resetSurfacePlacements();
    clearSymbols();
    selectVariantButton(initialState.variantButton);
    selectSurfaceButton(root.querySelector(`[data-surface-button][data-surface="${initialState.surface}"]`));
    selectSymbolCategory('social');
    updateText();
    updateFont();
    updatePrice();
    updateSummary();
    scheduleFit();
  };

  firstTextInput?.addEventListener('input', () => {
    textLines[0].text = firstTextInput.value;
    const characterCount = root.querySelector('[data-character-count]');
    if (characterCount) characterCount.textContent = String(firstTextInput.value.length);
    updateText();
  });
  root.querySelectorAll('[data-font-input]').forEach((input) => {
    input.addEventListener('change', () => {
      updateFont();
    });
  });
  root.querySelectorAll('[data-color-input]').forEach((input) => {
    input.addEventListener('change', () => {
      invalidateProductionFile();
      if (previewArt) previewArt.style.color = input.dataset.color;
      updateSummary();
    });
  });
  root.querySelectorAll('[data-symbol-add]').forEach((button) => {
    const iconTarget = button.querySelector('[data-symbol-option-icon]');
    if (iconTarget) iconTarget.innerHTML = vectorizer?.iconMarkup(button.dataset.symbol) || '';
    button.addEventListener('click', () => addSymbol(button.dataset.symbol));
  });
  root.querySelector('[data-symbol-clear]')?.addEventListener('click', clearSymbols);
  symbolClearAll?.addEventListener('click', clearSymbols);
  symbolJump?.addEventListener('click', () => {
    if (window.matchMedia('(max-width: 860px)').matches) setMobilePreview(true);
    else preview?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => symbolLayer?.querySelector(`[data-symbol-id="${activeSymbolId}"]`)?.focus(), 450);
  });
  root.querySelectorAll('[data-symbol-dock]').forEach((button) => button.addEventListener('click', () => dockActiveSymbol(button.dataset.symbolDock)));
  root.querySelectorAll('[data-symbol-size]').forEach((button) => button.addEventListener('click', () => resizeActiveSymbol(Number(button.dataset.symbolSize || 0))));
  root.querySelector('[data-symbol-done]')?.addEventListener('click', finishSymbolEditing);
  root.querySelector('[data-symbol-remove]')?.addEventListener('click', removeActiveSymbol);
  root.querySelectorAll('[data-symbol-category-button]').forEach((button) => {
    button.addEventListener('click', () => selectSymbolCategory(button.dataset.symbolCategoryButton));
  });

  root.querySelectorAll('[data-variant-button]').forEach((button) => {
    button.addEventListener('click', () => {
      selectVariantButton(button);
    });
  });

  root.querySelectorAll('[data-surface-button]').forEach((button) => {
    button.addEventListener('click', () => selectSurfaceButton(button));
  });

  previewScaleInput?.addEventListener('input', () => setPreviewScale(previewScaleInput.value));
  root.querySelectorAll('[data-preview-scale-step]').forEach((button) => {
    button.addEventListener('click', () => {
      const current = Number(previewScaleInput?.value || Math.round(currentPreviewScale() * 100));
      setPreviewScale(current + Number(button.dataset.previewScaleStep || 0));
    });
  });

  preview?.addEventListener('pointerdown', (event) => {
    if (!activeSymbol() || !(event.target instanceof Element)) return;
    if (event.target.closest('[data-symbol-object], [data-symbol-toolbar]')) return;
    finishSymbolEditing();
  });
  previewText?.addEventListener('pointerdown', beginPlacementDrag);
  previewText?.addEventListener('keydown', (event) => {
    const amount = event.shiftKey ? 0.05 : 0.015;
    const moves = { ArrowLeft: [-amount, 0], ArrowRight: [amount, 0], ArrowUp: [0, -amount], ArrowDown: [0, amount] };
    if (!moves[event.key]) return;
    event.preventDefault();
    nudgePlacement(...moves[event.key]);
  });

  resetDesignButton?.addEventListener('click', resetDesign);
  mobilePreviewOpen?.addEventListener('click', () => setMobilePreview(true));
  mobilePreviewCloseButtons.forEach((button) => button.addEventListener('click', () => setMobilePreview(false, { restoreFocus: true })));
  mobileSubmit?.addEventListener('click', () => {
    if (typeof form?.requestSubmit === 'function') form.requestSubmit(submitButton || undefined);
    else submitButton?.click();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activeSymbol()) {
      finishSymbolEditing();
      return;
    }
    if (event.key === 'Escape' && stage?.classList.contains('is-mobile-open')) setMobilePreview(false, { restoreFocus: true });
  });

  root.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
    if (!quantityInput) return;
    quantityInput.value = String(Math.max(1, Number(quantityInput.value || 1) - 1));
    updatePrice();
  });
  root.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
    if (!quantityInput) return;
    quantityInput.value = String(Math.min(99, Number(quantityInput.value || 1) + 1));
    updatePrice();
  });
  quantityInput?.addEventListener('input', updatePrice);
  mirrorInput?.addEventListener('change', () => {
    invalidateProductionFile();
    preview?.classList.toggle('is-mirror-order', mirrorInput.checked);
    updateSummary();
  });

  form?.addEventListener('submit', async (event) => {
    if (productionReady) return;
    event.preventDefault();
    if (!form.reportValidity()) return;

    if (!vectorizer || !productionFileInput || typeof DataTransfer === 'undefined') {
      if (productionError) {
        productionError.textContent = 'Din webbläsare kunde inte skapa produktionsfilen. Uppdatera sidan eller prova Safari/Chrome igen.';
        productionError.hidden = false;
      }
      return;
    }

    submitButton?.setAttribute('aria-busy', 'true');
    if (submitButton) submitButton.disabled = true;
    mobileSubmit?.setAttribute('aria-busy', 'true');
    if (mobileSubmit) mobileSubmit.disabled = true;
    if (submitLabel) submitLabel.textContent = 'FÖRBEREDER DIN DEKAL…';
    if (mobileSubmitLabel) mobileSubmitLabel.textContent = 'FÖRBEREDER…';
    if (productionError) productionError.hidden = true;

    try {
      updateSymbolFields();
      updateSummary();
      const beforeKey = productionPayloadKey();
      const payload = designerProductionPayload();
      const result = checkedProductionKey === beforeKey && checkedProductionResult
        ? checkedProductionResult : await vectorizer.generateProductionFile(payload);
      if (productionPayloadKey() !== beforeKey) throw new Error('Din design ändrades. Kontrollera den och försök igen.');
      validateDesignerProduction(result, payload);
      const transfer = new DataTransfer();
      transfer.items.add(result.file);
      productionFileInput.files = transfer.files;
      if (productionFileInput.files.length !== 1) throw new Error('Produktionsfilen kunde inte kopplas till ordern.');

      const heightText = result.heightMm.toLocaleString('sv-SE', { maximumFractionDigits: 1 });
      if (heightMm) heightMm.textContent = heightText;
      if (productionMeasureProperty) productionMeasureProperty.value = `${result.widthMm} × ${heightText} mm`;
      if (designIdProperty) designIdProperty.value = `SD-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
      productionReady = true;
      form.submit();
    } catch (error) {
      if (productionError) {
        productionError.textContent = error?.message || 'Produktionsfilen kunde inte skapas. Försök igen.';
        productionError.hidden = false;
      }
      submitButton?.removeAttribute('aria-busy');
      if (submitButton) submitButton.disabled = false;
      mobileSubmit?.removeAttribute('aria-busy');
      if (mobileSubmit) mobileSubmit.disabled = false;
      if (submitLabel) submitLabel.textContent = 'LÄGG I VARUKORG';
      if (mobileSubmitLabel) mobileSubmitLabel.textContent = 'LÄGG I VARUKORG →';
    }
  });

  const productionCheck = root.querySelector('[data-production-check]');
  let productionCheckTimer = 0;
  let productionCheckVersion = 0;
  let checkedProductionKey = '';
  let checkedProductionResult = null;
  let pendingProductionKey = '';

  function updateFontSamples() {
    const text = (firstTextInput?.value || '').trim() || 'Åk med stil – ÅÄÖ';
    root.querySelectorAll('[data-font-sample]').forEach((sample) => { sample.textContent = text; });
    updateSelectedFont();
  }

  function updateSelectedFont() {
    const summary = root.querySelector('.sd-font-summary');
    const input = selectedFont();
    if (!summary || !input) return;
    summary.style.setProperty('--choice-font', '"' + input.dataset.font + '"');
    summary.querySelector('[data-selected-font-name]').textContent = input.value;
  }

  const fontDropdown = root.querySelector('[data-font-dropdown]');
  if (fontDropdown) {
    const summary = fontDropdown.querySelector('.sd-font-summary');
    const menu = fontDropdown.querySelector('[data-font-grid]');
    const closeFontMenu = (restoreFocus = false) => {
      menu.hidden = true;
      summary.setAttribute('aria-expanded', 'false');
      fontDropdown.classList.remove('is-open');
      if (restoreFocus) summary.focus({ preventScroll: true });
    };
    summary.addEventListener('click', () => {
      if (!menu.hidden) return closeFontMenu();
      menu.hidden = false;
      summary.setAttribute('aria-expanded', 'true');
      fontDropdown.classList.add('is-open');
      const choice = selectedFont()?.closest('.font-choice');
      if (choice) menu.scrollTop += choice.getBoundingClientRect().top - menu.getBoundingClientRect().top - 11;
    });
    fontDropdown.addEventListener('click', (event) => {
      if (event.target.matches('[data-font-input]') && event.detail > 0) window.requestAnimationFrame(() => closeFontMenu(true));
    });
    fontDropdown.addEventListener('keydown', (event) => {
      if (event.key === 'Tab') window.setTimeout(() => {
        if (!fontDropdown.contains(document.activeElement)) closeFontMenu();
      }, 0);
      if (!menu.hidden && (event.key === 'Escape' || (event.key === 'Enter' && event.target.matches('[data-font-input]')))) {
        event.preventDefault();
        closeFontMenu(true);
      }
    });
    document.addEventListener('pointerdown', (event) => {
      if (!menu.hidden && !fontDropdown.contains(event.target)) closeFontMenu();
    });
  }

  function designerProductionPayload() {
    return {
      lines: textLines.map((line) => ({text:line.text.normalize('NFC'),fontUrl:line.fontUrl,fontName:line.fontName,scale:line.scale})),
      color:selectedColor()?.dataset.color,
      colorName:selectedColor()?.value,
      widthMm:selectedWidthCm()*10,
      symbols:layoutPayload().symbols,
      mirror:Boolean(mirrorInput?.checked),
      specification:specificationProperty?.value,
    };
  }

  function productionPayloadKey() { return JSON.stringify(designerProductionPayload()); }

  function productionTextHeight(result) {
    const source = result.svg.match(/<metadata>([\s\S]*?)<\/metadata>/)?.[1];
    const viewBox = result.svg.match(/viewBox="([^"]+)"/)?.[1].trim().split(/\s+/).map(Number);
    if (!source || !viewBox || !(viewBox[2] > 0)) throw new Error('Textens mått kunde inte kontrolleras. Försök igen.');
    const metadata = JSON.parse(source.replaceAll('&quot;','"').replaceAll('&apos;',"'").replaceAll('&lt;','<').replaceAll('&gt;','>').replaceAll('&amp;','&'));
    if (!metadata.lines?.length) throw new Error('Textens mått saknas. Försök igen.');
    const height = Math.min(...metadata.lines.map((line) => Number(line.height))) * Number(result.widthMm) / viewBox[2];
    if (!(height > 0) || !Number.isFinite(height)) throw new Error('Textens mått kunde inte kontrolleras. Försök igen.');
    return height;
  }

  function validateDesignerProduction(result, payload) {
    if (payload.widthMm < 300) throw new Error('Minsta bredd är 30 cm. Välj en större bredd.');
    if (payload.lines.some((line) => !line.text.trim())) throw new Error('Skriv din text för att fortsätta.');
    const textHeight = productionTextHeight(result);
    if (textHeight < 20) {
      const minimumWidth = Math.ceil(payload.widthMm * 20 / textHeight / 100) * 10;
      const nextWidth = [...root.querySelectorAll('[data-variant-record]')].map((record) => Number(record.dataset.width)).filter((width) => width >= minimumWidth).sort((a,b) => a-b)[0];
      throw new Error(`Texten blir ${(textHeight/10).toLocaleString('sv-SE',{maximumFractionDigits:1})} cm hög. Minsta texthöjd är 2 cm. ${nextWidth ? 'Välj minst '+nextWidth+' cm bredd eller korta texten.' : 'Korta texten eller välj ett högre typsnitt.'}`);
    }
    return textHeight;
  }

  function setProductionCheck(message, state, ready) {
    if (productionCheck) { productionCheck.textContent=message; productionCheck.dataset.state=state; }
    const summaryState = root.querySelector('.designer-order-summary-head small');
    if (summaryState) summaryState.textContent = ready ? '✓ DIN DESIGN KLAR' : state === 'invalid' ? 'KONTROLLERA DIN DESIGN' : 'KONTROLLERAR DIN DESIGN';
    const available = selectedVariant()?.dataset.available !== 'false' && selectedWidthCm() >= 30;
    if (!submitButton?.hasAttribute('aria-busy')) {
      if (submitButton) submitButton.disabled = !ready || !available;
      if (mobileSubmit) mobileSubmit.disabled = !ready || !available;
    }
  }

  function scheduleProductionCheck() {
    const payload = designerProductionPayload();
    const key = JSON.stringify(payload);
    if (key === checkedProductionKey || key === pendingProductionKey) return;
    clearTimeout(productionCheckTimer);
    const version = ++productionCheckVersion;
    pendingProductionKey = key;
    checkedProductionKey = '';
    checkedProductionResult = null;
    if (payload.lines.some((line) => !line.text.trim())) {
      pendingProductionKey = '';
      setProductionCheck('Skriv din text för att fortsätta.','invalid',false);
      return;
    }
    setProductionCheck('Kontrollerar textens mått…','checking',false);
    productionCheckTimer = setTimeout(async () => {
      try {
        if (!vectorizer?.generateProductionFile) throw new Error('Textens mått kunde inte kontrolleras. Ladda om sidan.');
        const result = await vectorizer.generateProductionFile(payload);
        if (version !== productionCheckVersion) return;
        const textHeight = validateDesignerProduction(result,payload);
        checkedProductionKey=key;
        checkedProductionResult=result;
        const h=result.heightMm.toLocaleString('sv-SE',{maximumFractionDigits:1});
        if (heightMm) heightMm.textContent=h;
        measureTextTargets.forEach((target) => { target.textContent=`${result.widthMm} × ${h} mm`; });
        if (productionMeasureProperty) productionMeasureProperty.value=`${result.widthMm} × ${h} mm`;
        setProductionCheck(`Texthöjd: ${(textHeight/10).toLocaleString('sv-SE',{maximumFractionDigits:1})} cm · minst 2 cm`,'valid',true);
      } catch (error) {
        if (version !== productionCheckVersion) return;
        if (heightMm) heightMm.textContent='—';
        measureTextTargets.forEach((target) => { target.textContent=`${payload.widthMm} × — mm`; });
        setProductionCheck(error?.message || 'Textens mått kunde inte kontrolleras. Försök igen.','invalid',false);
      } finally {
        if (version === productionCheckVersion) pendingProductionKey='';
      }
    },250);
  }

  syncVariantForDesign();
  updateText();
  updateFont();
  updatePrice();
  updateSymbolFields();
  updateSummary();
  selectSymbolCategory('social');
  syncPreviewScaleControl();
  applyPreviewTransform();
  applySurfacePlacement();
  document.fonts?.ready.then(scheduleFit);
  window.addEventListener('resize', scheduleFit, { passive: true });
}

function initStandardProduct(root) {
  const form = root.querySelector('form');
  const variantInput = root.querySelector('[data-standard-variant-input]');
  const variantRecords = [...root.querySelectorAll('[data-standard-variant-record]')];
  const widthButtons = [...root.querySelectorAll('[data-standard-width-button]')];
  const fontInputs = [...root.querySelectorAll('[data-standard-font-input]')];
  const colorInputs = [...root.querySelectorAll('[data-standard-color-input]')];
  const directionButtons = [...root.querySelectorAll('[data-standard-direction-button]')];
  const mirrorInput = root.querySelector('[data-standard-mirror-input]');
  const preview = root.querySelector('[data-standard-preview]');
  const previewText = root.querySelector('[data-standard-preview-text]');
  const previewArtworkWrap = root.querySelector('[data-standard-preview-artwork-wrap]');
  const mirrorBadge = root.querySelector('[data-standard-mirror-badge]');
  const widthProperty = root.querySelector('[data-standard-width-property]');
  const fontProperty = root.querySelector('[data-standard-font-property]');
  const colorProperty = root.querySelector('[data-standard-color-property]');
  const directionProperty = root.querySelector('[data-standard-direction-property]');
  const unitPriceTargets = root.querySelectorAll('[data-standard-unit-price]');
  const totalPriceTargets = root.querySelectorAll('[data-standard-total-price]');
  const priceNote = root.querySelector('[data-standard-price-note]');
  const quantityInput = root.querySelector('[data-standard-quantity]');
  const submitButton = root.querySelector('[data-standard-submit]');
  const submitLabel = root.querySelector('[data-standard-submit-label]');
  const mobileSubmitButton = root.querySelector('[data-standard-mobile-submit]');
  const mobileSubmitLabel = root.querySelector('[data-standard-mobile-submit-label]');
  const variantMessage = root.querySelector('[data-standard-variant-message]');
  const summaryWidth = root.querySelector('[data-standard-summary-width]');
  const summaryFont = root.querySelector('[data-standard-summary-font]');
  const summaryColor = root.querySelector('[data-standard-summary-color]');
  const summaryDirection = root.querySelector('[data-standard-summary-direction]');
  const summaryMounting = root.querySelector('[data-standard-summary-mounting]');
  const productionMeasureProperty = root.querySelector('[data-standard-production-measure-property]');
  const specificationProperty = root.querySelector('[data-standard-specification-property]');
  const designIdProperty = root.querySelector('[data-standard-design-id-property]');
  const productionFileInput = root.querySelector('[data-standard-production-file]');
  const productionError = root.querySelector('[data-standard-production-error]');
  const widthMmTarget = root.querySelector('[data-standard-width-mm]');
  const heightMmTarget = root.querySelector('[data-standard-height-mm]');
  const vectorizer = window.SwedeDecalVectorizer;
  const hasColorVariants = root.dataset.colorVariants === 'true';
  const hasDirectionVariants = root.dataset.directionVariants === 'true';
  const sideChoiceMode = root.dataset.sideChoice === 'true';
  const hasFixedArtwork = root.dataset.fixedArtwork === 'true';
  const artworkUrl = root.dataset.artworkUrl || '';
  const artworkId = root.dataset.artworkId || '';
  let productionReady = false;
  let productionResult = null;
  let productionKey = '';
  let artworkPromise = null;
  let measurementTimer = 0;
  let measurementToken = 0;
  if (form) {
    form.enctype = 'multipart/form-data';
    form.encoding = 'multipart/form-data';
  }
  const normalise = (value) => String(value || '').trim().toLocaleLowerCase('sv');
  const formatSek = (cents) => new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: 'SEK',
    maximumFractionDigits: 0,
  }).format(cents / 100);

  const selectedWidthButton = () => root.querySelector('[data-standard-width-button].is-selected');
  const selectedFont = () => root.querySelector('[data-standard-font-input]:checked') || fontInputs[0];
  const selectedColor = () => root.querySelector('[data-standard-color-input]:checked') || colorInputs[0];
  const selectedDirectionButton = () => root.querySelector('[data-standard-direction-button].is-selected') || directionButtons[0];
  const widthToMm = (value) => {
    const label = String(value || '').trim().toLocaleLowerCase('sv');
    const amount = Number.parseFloat(label.replace(',', '.'));
    if (!Number.isFinite(amount)) return 0;
    if (label.includes('mm')) return amount;
    return amount * 10;
  };
  const formatMm = (value) => Number(value || 0).toLocaleString('sv-SE', { maximumFractionDigits: 1 });
  const recordFor = (width, color, direction) => {
    let matches = variantRecords.filter((record) => normalise(record.dataset.width) === normalise(width));
    if (hasColorVariants) matches = matches.filter((record) => normalise(record.dataset.color) === normalise(color));
    if (hasDirectionVariants) matches = matches.filter((record) => normalise(record.dataset.direction) === normalise(direction));
    return matches.find((record) => record.dataset.available === 'true') || matches[0] || null;
  };

  const loadFixedArtwork = () => {
    if (!hasFixedArtwork) return Promise.resolve(null);
    if (!artworkUrl) return Promise.reject(new Error('Motivets originalfil saknas i temat.'));
    if (!artworkPromise) {
      artworkPromise = fetch(artworkUrl, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`Motivets originalfil kunde inte hämtas (${response.status}).`);
          return response.text();
        })
        .then((source) => {
          const documentNode = new DOMParser().parseFromString(source, 'image/svg+xml');
          if (documentNode.querySelector('parsererror')) throw new Error('Motivets SVG-fil är skadad.');
          const svg = documentNode.querySelector('svg');
          const pathData = [...documentNode.querySelectorAll('path')]
            .map((path) => path.getAttribute('d') || '')
            .filter(Boolean)
            .join(' ');
          if (!svg || !pathData) throw new Error('Motivets SVG-fil innehåller inga skärbara banor.');
          return {
            pathData,
            viewBox: svg.getAttribute('viewBox') || '',
            title: root.querySelector('.standard-product-title-row h1')?.textContent?.trim() || artworkId || 'Färdigt motiv',
          };
        });
    }
    return artworkPromise;
  };

  const updateSpecification = () => {
    const width = selectedWidthButton()?.dataset.width || '';
    const font = selectedFont();
    const color = selectedColor();
    const direction = selectedDirectionButton()?.dataset.direction || '';
    if (specificationProperty) {
      specificationProperty.value = [
        hasFixedArtwork ? `MOTIV-${artworkId || 'FAST'}` : 'FARDIG',
        hasFixedArtwork ? '' : (font?.dataset.code || font?.value),
        color?.dataset.code || color?.value,
        `${formatMm(widthToMm(width))}MM`,
        direction ? normalise(direction).replace(/[^a-z0-9]+/g, '-').toUpperCase() : '',
        mirrorInput?.checked ? 'MIRROR' : 'NORMAL',
        hasFixedArtwork ? 'ORIGINAL-SVG-V8' : 'OUTLINED-SVG-V7',
      ].filter(Boolean).join('-');
    }
  };

  const clearProductionFile = () => {
    if (!productionFileInput) return;
    try { productionFileInput.value = ''; } catch (_) { /* Shopify/webbläsaren tömmer filfältet när det går. */ }
    if (productionFileInput.files?.length) {
      try {
        const transfer = new DataTransfer();
        productionFileInput.files = transfer.files;
      } catch (_) { /* Fältet ersätts vid nästa lyckade generering. */ }
    }
  };

  const invalidateProductionFile = ({ keepDimensions = false } = {}) => {
    productionReady = false;
    productionResult = null;
    productionKey = '';
    measurementToken += 1;
    clearProductionFile();
    if (productionError) productionError.hidden = true;
    if (!keepDimensions && heightMmTarget) heightMmTarget.textContent = '…';
  };

  const productionPayload = (artwork = null) => {
    const font = selectedFont();
    const color = selectedColor();
    const text = previewText?.textContent?.trim() || '';
    return {
      text,
      fontUrl: hasFixedArtwork ? '' : (font?.dataset.fontUrl || ''),
      fontName: hasFixedArtwork ? '' : (font?.value || font?.dataset.font || ''),
      color: color?.dataset.color || '#f5f3ec',
      colorName: color?.value || '',
      widthMm: widthToMm(selectedWidthButton()?.dataset.width),
      symbols: [],
      mirror: Boolean(mirrorInput?.checked),
      specification: specificationProperty?.value || '',
      artworkPath: artwork?.pathData || '',
      artworkViewBox: artwork?.viewBox || '',
      artworkTitle: artwork?.title || '',
      artworkId,
      direction: sideChoiceMode ? '' : (selectedDirectionButton()?.dataset.direction || ''),
      sideChoice: sideChoiceMode ? (selectedDirectionButton()?.dataset.direction || '') : '',
    };
  };

  const payloadKey = (payload) => JSON.stringify([
    payload.text,
    payload.fontUrl,
    payload.fontName,
    payload.color,
    payload.colorName,
    payload.widthMm,
    payload.mirror,
    payload.specification,
    payload.artworkPath,
    payload.artworkId,
    payload.direction,
    payload.sideChoice,
  ]);

  const applyProductionMeasurements = (result) => {
    if (!result) return;
    const widthText = formatMm(result.widthMm);
    const heightText = formatMm(result.heightMm);
    if (widthMmTarget) widthMmTarget.textContent = widthText;
    if (heightMmTarget) heightMmTarget.textContent = heightText;
    if (productionMeasureProperty) {
      productionMeasureProperty.value = result.itemCount > 1
        ? `${result.itemCount} st × ${widthText} × ${heightText} mm`
        : `${widthText} × ${heightText} mm`;
    }
  };

  const generateProductionResult = async ({ attach = false } = {}) => {
    if (!vectorizer?.generateProductionFile) throw new Error('Vektormotorn kunde inte startas. Uppdatera sidan och försök igen.');
    updateSpecification();
    const artwork = hasFixedArtwork ? await loadFixedArtwork() : null;
    const payload = productionPayload(artwork);
    if (!payload.widthMm || (hasFixedArtwork ? !payload.artworkPath : (!payload.text || !payload.fontUrl))) {
      throw new Error('Motiv, typsnitt eller bredd saknas.');
    }
    const key = payloadKey(payload);
    let result = productionResult && productionKey === key ? productionResult : null;
    if (!result) result = await vectorizer.generateProductionFile(payload);
    if (sideChoiceMode && result?.file && result?.svg) {
      const sideLabel = selectedDirectionButton()?.dataset.direction || '';
      const sideSuffix = normalise(sideLabel).includes('vänster') ? 'vanster' : 'hoger';
      const renamedFilename = String(result.filename || 'swededecal-motiv.svg')
        .replace(/-(?:hoger|vanster|par)\.svg$/i, `-${sideSuffix}.svg`);
      result = { ...result, filename: renamedFilename, file: new File([result.svg], renamedFilename, { type: 'image/svg+xml' }) };
    }
    if (payloadKey(productionPayload(artwork)) !== key) return null;
    productionResult = result;
    productionKey = key;
    applyProductionMeasurements(result);

    if (attach) {
      if (!productionFileInput || typeof DataTransfer === 'undefined') throw new Error('Din webbläsare kunde inte bifoga produktionsfilen. Prova Safari eller Chrome igen.');
      const transfer = new DataTransfer();
      transfer.items.add(result.file);
      productionFileInput.files = transfer.files;
      if (productionFileInput.files.length !== 1) throw new Error('Produktionsfilen kunde inte kopplas till ordern.');
    }
    return result;
  };

  const scheduleMeasurements = () => {
    window.clearTimeout(measurementTimer);
    const width = widthToMm(selectedWidthButton()?.dataset.width);
    if (widthMmTarget) widthMmTarget.textContent = formatMm(width);
    if (heightMmTarget) heightMmTarget.textContent = '…';
    const token = ++measurementToken;
    measurementTimer = window.setTimeout(async () => {
      try {
        const result = await generateProductionResult();
        if (token !== measurementToken || !result) return;
        applyProductionMeasurements(result);
      } catch (_) {
        if (token === measurementToken && heightMmTarget) heightMmTarget.textContent = '—';
      }
    }, 25);
  };

  const fitPreview = () => {
    if (hasFixedArtwork || !preview || !previewText) return;
    const isCompactMobile = window.innerWidth <= 560;
    const availableWidth = Math.max(180, preview.clientWidth - (isCompactMobile ? 36 : 84));
    const availableHeight = Math.max(100, preview.clientHeight - (isCompactMobile ? 72 : 110));
    const baseSize = isCompactMobile
      ? Math.min(72, Math.max(44, preview.clientWidth * 0.14))
      : Math.min(112, Math.max(72, preview.clientWidth * 0.13));
    previewText.style.fontSize = `${baseSize}px`;
    const bounds = previewText.getBoundingClientRect();
    const ratio = Math.min(1, availableWidth / Math.max(1, bounds.width), availableHeight / Math.max(1, bounds.height));
    previewText.style.fontSize = `${Math.max(20, baseSize * ratio).toFixed(2)}px`;
  };

  const updateFont = () => {
    if (hasFixedArtwork) return;
    const input = selectedFont();
    if (!input) return;
    fontInputs.forEach((item) => item.closest('.font-choice')?.classList.toggle('is-selected', item === input));
    if (fontProperty) fontProperty.value = input.value;
    if (summaryFont) summaryFont.textContent = input.value;
    if (previewText) previewText.style.fontFamily = input.dataset.font || input.value;
    const fitAfterLoad = () => window.requestAnimationFrame(fitPreview);
    if (document.fonts?.load) document.fonts.load(`32px "${input.dataset.font || input.value}"`).then(fitAfterLoad, fitAfterLoad);
    else fitAfterLoad();
  };

  const updateColor = () => {
    const input = selectedColor();
    if (!input) return;
    colorInputs.forEach((item) => item.closest('.color-swatch')?.classList.toggle('is-selected', item === input));
    if (colorProperty) colorProperty.value = input.value;
    if (summaryColor) summaryColor.textContent = input.value;
    if (previewText) previewText.style.color = input.dataset.color || '#f5f3ec';
    if (previewArtworkWrap) previewArtworkWrap.style.setProperty('--decal-color', input.dataset.color || '#f5f3ec');
  };

  const updateDirection = () => {
    const button = selectedDirectionButton();
    const direction = button?.dataset.direction || '';
    directionButtons.forEach((item) => {
      const active = item === button;
      item.classList.toggle('is-selected', active);
      item.setAttribute('aria-pressed', String(active));
    });
    const value = normalise(direction);
    previewArtworkWrap?.classList.toggle('is-facing-left', !sideChoiceMode && value.includes('vänster'));
    previewArtworkWrap?.classList.toggle('is-pair', !sideChoiceMode && value.includes('par'));
    if (directionProperty) directionProperty.value = direction;
    if (summaryDirection) summaryDirection.textContent = direction;
  };

  const updateMounting = () => {
    const mirrored = Boolean(mirrorInput?.checked);
    preview?.classList.toggle('is-mirror-order', mirrored);
    if (mirrorBadge) mirrorBadge.hidden = !mirrored;
    if (summaryMounting) summaryMounting.textContent = mirrored ? 'Insida glas · spegelvänd' : 'Utsida / slät yta';
  };

  const updateChoiceAvailability = (width, color, direction) => {
    widthButtons.forEach((button) => {
      const matchingRecord = recordFor(button.dataset.width, color, direction);
      const anyAvailable = Boolean(matchingRecord && matchingRecord.dataset.available === 'true');
      button.disabled = !anyAvailable;
      const pricedRecord = matchingRecord
        || variantRecords.find((record) => normalise(record.dataset.width) === normalise(button.dataset.width));
      const target = button.querySelector('[data-standard-size-price]');
      if (target && pricedRecord) target.textContent = pricedRecord.dataset.price || '';
    });

    colorInputs.forEach((input) => {
      const matchingRecord = recordFor(width, input.value, direction);
      const unavailable = hasColorVariants && (!matchingRecord || matchingRecord.dataset.available !== 'true');
      input.disabled = unavailable;
      input.closest('.color-swatch')?.classList.toggle('is-unavailable', unavailable);
    });

    directionButtons.forEach((button) => {
      const matchingRecord = recordFor(width, color, button.dataset.direction);
      const unavailable = hasDirectionVariants && (!matchingRecord || matchingRecord.dataset.available !== 'true');
      button.disabled = unavailable;
      button.classList.toggle('is-unavailable', unavailable);
      const target = button.querySelector('[data-standard-direction-price]');
      if (target && matchingRecord) target.textContent = matchingRecord.dataset.price || '';
    });
  };

  const updatePrice = (record) => {
    const quantity = Math.min(99, Math.max(1, Number.parseInt(quantityInput?.value || '1', 10) || 1));
    if (quantityInput) quantityInput.value = String(quantity);
    const cents = Number(record?.dataset.priceCents || 0);
    const unitText = record?.dataset.price || '';
    unitPriceTargets.forEach((target) => { target.textContent = unitText; });
    totalPriceTargets.forEach((target) => { target.textContent = cents ? formatSek(cents * quantity) : unitText; });
    if (priceNote) priceNote.textContent = unitText ? `${unitText} / st` : '';
  };

  const syncVariant = () => {
    const widthButton = selectedWidthButton() || widthButtons.find((button) => !button.disabled) || widthButtons[0];
    const colorInput = selectedColor();
    const directionButton = selectedDirectionButton();
    const width = widthButton?.dataset.width || '';
    const color = colorInput?.value || '';
    const direction = directionButton?.dataset.direction || '';
    const record = recordFor(width, color, direction);
    const available = Boolean(record && record.dataset.available === 'true');

    updateChoiceAvailability(width, color, direction);
    if (variantInput && record) variantInput.value = record.dataset.variantId;
    if (widthProperty) widthProperty.value = width;
    if (summaryWidth) summaryWidth.textContent = width;
    if (summaryColor) summaryColor.textContent = color;
    if (summaryDirection) summaryDirection.textContent = direction;
    if (widthMmTarget) widthMmTarget.textContent = formatMm(widthToMm(width));
    updateSpecification();
    updatePrice(record);

    if (submitButton) submitButton.disabled = !available;
    if (mobileSubmitButton) mobileSubmitButton.disabled = !available;
    if (submitLabel) submitLabel.textContent = available ? 'LÄGG I KUNDVAGN' : 'SLUTSÅLD';
    if (mobileSubmitLabel) mobileSubmitLabel.textContent = available ? 'LÄGG I KUNDVAGN' : 'SLUTSÅLD';
    if (variantMessage) variantMessage.hidden = available;
  };

  widthButtons.forEach((button) => {
    button.addEventListener('click', () => {
      if (button.disabled) return;
      widthButtons.forEach((item) => {
        const active = item === button;
        item.classList.toggle('is-selected', active);
        item.setAttribute('aria-pressed', String(active));
      });
      invalidateProductionFile();
      syncVariant();
      scheduleMeasurements();
    });
  });

  fontInputs.forEach((input) => input.addEventListener('change', () => {
    invalidateProductionFile();
    updateFont();
    updateSpecification();
    scheduleMeasurements();
  }));
  colorInputs.forEach((input) => input.addEventListener('change', () => {
    invalidateProductionFile({ keepDimensions: true });
    updateColor();
    syncVariant();
    scheduleMeasurements();
  }));
  directionButtons.forEach((button) => button.addEventListener('click', () => {
    if (button.disabled) return;
    directionButtons.forEach((item) => {
      const active = item === button;
      item.classList.toggle('is-selected', active);
      item.setAttribute('aria-pressed', String(active));
    });
    invalidateProductionFile();
    updateDirection();
    syncVariant();
    scheduleMeasurements();
  }));
  mirrorInput?.addEventListener('change', () => {
    invalidateProductionFile({ keepDimensions: true });
    updateMounting();
    updateSpecification();
    scheduleMeasurements();
  });

  root.querySelector('[data-standard-quantity-minus]')?.addEventListener('click', () => {
    if (!quantityInput) return;
    quantityInput.value = String(Math.max(1, Number(quantityInput.value || 1) - 1));
    syncVariant();
  });
  root.querySelector('[data-standard-quantity-plus]')?.addEventListener('click', () => {
    if (!quantityInput) return;
    quantityInput.value = String(Math.min(99, Number(quantityInput.value || 1) + 1));
    syncVariant();
  });
  quantityInput?.addEventListener('input', syncVariant);

  mobileSubmitButton?.addEventListener('click', () => {
    if (!form || mobileSubmitButton.disabled) return;
    if (typeof form.requestSubmit === 'function') form.requestSubmit(submitButton || undefined);
    else submitButton?.click();
  });

  form?.addEventListener('submit', async (event) => {
    if (productionReady) return;
    event.preventDefault();
    syncVariant();
    if (submitButton?.disabled || !form.reportValidity()) return;

    if (!vectorizer || !productionFileInput || typeof DataTransfer === 'undefined') {
      if (productionError) {
        productionError.textContent = 'Din webbläsare kunde inte skapa produktionsfilen. Uppdatera sidan eller prova Safari/Chrome igen.';
        productionError.hidden = false;
      }
      return;
    }

    submitButton?.setAttribute('aria-busy', 'true');
    mobileSubmitButton?.setAttribute('aria-busy', 'true');
    if (submitButton) submitButton.disabled = true;
    if (mobileSubmitButton) mobileSubmitButton.disabled = true;
    if (submitLabel) submitLabel.textContent = 'FÖRBEREDER SVG…';
    if (mobileSubmitLabel) mobileSubmitLabel.textContent = 'FÖRBEREDER…';
    if (productionError) productionError.hidden = true;

    try {
      const result = await generateProductionResult({ attach: true });
      if (!result) throw new Error('Valen ändrades medan produktionsfilen skapades. Försök igen.');
      if (designIdProperty) designIdProperty.value = `SD-FARDIG-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
      productionReady = true;
      form.submit();
    } catch (error) {
      if (productionError) {
        productionError.textContent = error?.message || 'Produktionsfilen kunde inte skapas. Försök igen.';
        productionError.hidden = false;
      }
      submitButton?.removeAttribute('aria-busy');
      mobileSubmitButton?.removeAttribute('aria-busy');
      if (submitButton) submitButton.disabled = false;
      if (mobileSubmitButton) mobileSubmitButton.disabled = false;
      if (submitLabel) submitLabel.textContent = 'LÄGG I KUNDVAGN';
      if (mobileSubmitLabel) mobileSubmitLabel.textContent = 'LÄGG I KUNDVAGN';
    }
  });

  updateFont();
  updateColor();
  updateDirection();
  updateMounting();
  syncVariant();
  updateSpecification();
  scheduleMeasurements();
  window.requestAnimationFrame(fitPreview);
  document.fonts?.ready.then(fitPreview);
  window.addEventListener('resize', fitPreview, { passive: true });
}

function initCatalogFilter() {
  const input = document.querySelector('[data-product-filter]');
  const grid = document.querySelector('[data-product-grid]');
  const counter = document.querySelector('[data-visible-count]');
  if (!input || !grid) return;

  input.addEventListener('input', () => {
    const query = input.value.trim().toLocaleLowerCase('sv');
    let visible = 0;
    grid.querySelectorAll('[data-product-card]').forEach((card) => {
      const haystack = `${card.dataset.title || ''} ${card.dataset.tags || ''}`.toLocaleLowerCase('sv');
      const match = !query || haystack.includes(query);
      card.hidden = !match;
      if (match) visible += 1;
    });
    if (counter) counter.textContent = String(visible);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initMenu();
  document.querySelectorAll('[data-designer]').forEach(initDesigner);
  document.querySelectorAll('[data-standard-product]').forEach(initStandardProduct);
  initCatalogFilter();
});

